// src/modules/time-tracking/components/ShiftView.js
// Shared shift detail: summary header + Timeline / Map tabs. Layout-agnostic —
// used by the admin ShiftDetail page and the employee MyShiftDetail page.
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FaClock, FaMapMarkerAlt, FaRoute, FaFlagCheckered, FaSignInAlt, FaSignOutAlt,
  FaExclamationTriangle, FaBuilding, FaBriefcase, FaStopwatch,
} from 'react-icons/fa';
import { StatusBadge } from '../../../components/styled/CommonStyles';
import { formatDateTime } from '../../communications/utils/formatUtils';
import {
  formatHours, shiftSeconds, statusColor, statusLabel,
} from '../timeTrackingService';
import ShiftTrackMap from './ShiftTrackMap';

const Wrap = styled.div`display: flex; flex-direction: column; gap: ${(p) => p.theme.spacing(2)};`;

const SummaryCard = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
`;

const SummaryTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: ${(p) => p.theme.spacing(2)}; flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing(2)};
  h2 { margin: 0; color: ${(p) => p.theme.colors.primary}; font-size: ${(p) => p.theme.typography.fontSize.large}; }
`;

const Stats = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${(p) => p.theme.spacing(2)};
`;

const Stat = styled.div`
  background: ${(p) => p.theme.colors.background};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)};
  .label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small};
    display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
  .value { color: ${(p) => p.theme.colors.textPrimary}; font-size: 1.15rem; font-weight: 600; }
  svg { color: ${(p) => p.theme.colors.primary}; }
`;

const Tabs = styled.div`
  display: flex; gap: ${(p) => p.theme.spacing(1)}; margin: ${(p) => p.theme.spacing(1)} 0;
`;
const Tab = styled.button`
  background: ${(p) => (p.active ? p.theme.colors.primary : p.theme.colors.surface)};
  color: ${(p) => (p.active ? p.theme.colors.black : p.theme.colors.textPrimary)};
  border: 1px solid ${(p) => (p.active ? p.theme.colors.primary : p.theme.colors.inputGray)};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)};
  cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: .95rem;
  &:hover { background: ${(p) => p.theme.colors.primary}22; }
`;

const Panel = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
`;

const SectionTitle = styled.h3`
  color: ${(p) => p.theme.colors.textPrimary}; margin: 0 0 ${(p) => p.theme.spacing(2)};
  font-size: ${(p) => p.theme.typography.fontSize.medium};
  display: flex; align-items: center; gap: 8px;
  svg { color: ${(p) => p.theme.colors.primary}; }
`;

const TLItem = styled.div`
  display: flex; gap: ${(p) => p.theme.spacing(2)}; padding: ${(p) => p.theme.spacing(2)} 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.inputGray}22;
  &:last-child { border-bottom: none; }
  .icon { width: 34px; height: 34px; border-radius: 50%; flex: none;
    display: flex; align-items: center; justify-content: center;
    background: ${(p) => p.color || p.theme.colors.primary}22; color: ${(p) => p.color || p.theme.colors.primary}; }
  .body { flex: 1; min-width: 0; }
  .title { color: ${(p) => p.theme.colors.textPrimary}; font-weight: 600; }
  .meta { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small}; margin-top: 2px; }
`;

const Empty = styled.p`color: ${(p) => p.theme.colors.textSecondary}; text-align: center; padding: ${(p) => p.theme.spacing(3)};`;

const ShiftView = ({ detail }) => {
  const [tab, setTab] = useState('timeline');
  if (!detail || !detail.shift) return null;

  const { shift, pings = [] } = detail;
  const segments = shift.segments || [];
  const stops = shift.stops || [];
  const employeeName = shift.User?.username || `User #${shift.user_id}`;

  return (
    <Wrap>
      <SummaryCard>
        <SummaryTop>
          <h2>{employeeName}</h2>
          <StatusBadge color={statusColor(shift.status)}>{statusLabel(shift.status)}</StatusBadge>
        </SummaryTop>
        <Stats>
          <Stat>
            <div className="label"><FaSignInAlt /> Clock in</div>
            <div className="value">{formatDateTime(shift.clock_in_at)}</div>
          </Stat>
          <Stat>
            <div className="label"><FaSignOutAlt /> Clock out</div>
            <div className="value">{shift.clock_out_at ? formatDateTime(shift.clock_out_at) : 'Still active'}</div>
          </Stat>
          <Stat>
            <div className="label"><FaClock /> Total time</div>
            <div className="value">{formatHours(shiftSeconds(shift))}</div>
          </Stat>
          <Stat>
            <div className="label"><FaMapMarkerAlt /> Pings</div>
            <div className="value">{detail.pingCount ?? pings.length}{detail.pingsTruncated ? '+' : ''}</div>
          </Stat>
        </Stats>
        {shift.review_flag && (
          <div style={{ marginTop: 12, color: '#f39c12', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaExclamationTriangle /> Flagged for review (auto-closed or anomaly).
          </div>
        )}
      </SummaryCard>

      <Tabs>
        <Tab active={tab === 'timeline'} onClick={() => setTab('timeline')}><FaRoute /> Timeline</Tab>
        <Tab active={tab === 'map'} onClick={() => setTab('map')}><FaMapMarkerAlt /> Map</Tab>
      </Tabs>

      {tab === 'timeline' ? (
        <>
          <Panel>
            <SectionTitle><FaBriefcase /> Locations worked ({segments.length})</SectionTitle>
            {segments.length === 0 ? <Empty>No segments recorded.</Empty> : segments.map((s) => (
              <TLItem key={s.id} color="#F69620">
                <div className="icon">{s.location_type === 'site' ? <FaBuilding /> : <FaBriefcase />}</div>
                <div className="body">
                  <div className="title">{s.label || (s.location_type === 'site' ? 'Registered site' : 'Job location')}</div>
                  <div className="meta">
                    {formatDateTime(s.started_at)} → {s.ended_at ? formatDateTime(s.ended_at) : 'ongoing'}
                    {' · '}{s.location_type}
                  </div>
                </div>
              </TLItem>
            ))}
          </Panel>

          <Panel>
            <SectionTitle><FaStopwatch /> Stops off-site ({stops.length})</SectionTitle>
            {stops.length === 0 ? <Empty>No off-site stops detected.</Empty> : stops.map((st) => (
              /* Every entry here is off-site: the employee never logged this
                 site. is_known_location only says we RECOGNISE the spot (a
                 registered site, or a job scheduled that day), which usually
                 means a missed location update rather than a detour — amber,
                 not green, and never "this is fine". */
              <TLItem key={st.id} color={st.is_known_location ? '#e0a33e' : '#e74c3c'}>
                <div className="icon">{st.is_known_location ? <FaFlagCheckered /> : <FaExclamationTriangle />}</div>
                <div className="body">
                  <div className="title">{st.address || 'Unknown location'}</div>
                  <div className="meta">
                    {formatDateTime(st.started_at)}
                    {' · '}{Math.round((st.duration_seconds || 0) / 60)} min
                    {' · '}{st.is_known_location ? 'off-site · known site, not logged' : 'off-site'}
                  </div>
                </div>
              </TLItem>
            ))}
          </Panel>
        </>
      ) : (
        <Panel>
          <ShiftTrackMap pings={pings} stops={stops} segments={segments} />
        </Panel>
      )}
    </Wrap>
  );
};

export default ShiftView;
